// Derivaciones de la cosecha «1ª cuota a 14 días» — lógica PURA, sin React.
//
// Cubre el scatter de tiendas del C5 (cuadrantes, radio y color de cada punto) y las anomalías de
// la ventana. Los umbrales salen del mockup y son DOS números distintos que no se unifican:
// 60 % parte el plano en cuadrantes, 65 % es el piso saludable que dispara una anomalía.
import type {
    StoreCohortPoint,
    StoreOriginationRow,
    StoreQuadrant,
} from '@/api/services/retail-wizard/backoffice';
import { formatPct, formatUnits, storeLabel } from './originationMetrics';

/** Corte del eje X del scatter: SCI 60. El silo ya clasificó con este mismo número. */
export const STORE_QUADRANT_SCI_CUT = 60;

/** Corte del eje Y del scatter: cosecha 60 %. NO es el umbral de anomalía. */
export const STORE_QUADRANT_COHORT_CUT_PCT = 60;

/** Piso saludable de la cosecha: por debajo, la tienda entra en anomalías. NO es el corte del plano. */
export const COHORT_HEALTHY_PCT = 65;

/** Los ejes del mock: SCI de 20 a 90 y cosecha de 0 a 100 %. Fijos, no salen de los datos. */
export const SCATTER_SCI_MIN = 20;
export const SCATTER_SCI_MAX = 90;

/** Los dos guardas de las anomalías: 15 pp de caída de captura y 3 de muestra mínima. */
export const ANOMALY_CAPTURE_DROP_PP = -15;
export const ANOMALY_MIN_SAMPLE = 3;

const QUADRANT_LABEL: Record<StoreQuadrant, string> = {
    LUCKY: 'SUERTE',
    MODEL: 'MODELO',
    PROBLEM: 'PROBLEMA',
    SOMETHING_OFF: 'ALGO PASA',
};

/** Qué significa cada zona, para el `title` del punto y la leyenda. */
const QUADRANT_MEANING: Record<StoreQuadrant, string> = {
    LUCKY: 'SCI bajo pero cosecha alta — el motor va conservador',
    MODEL: 'SCI alto y cosecha alta — el motor calibra bien',
    PROBLEM: 'SCI bajo y cosecha baja — tienda a intervenir',
    SOMETHING_OFF: 'SCI alto pero cosecha baja — buen cliente y algo lo tumba',
};

export function quadrantLabel(quadrant: StoreQuadrant): string {
    return QUADRANT_LABEL[quadrant];
}

export function quadrantMeaning(quadrant: StoreQuadrant): string {
    return QUADRANT_MEANING[quadrant];
}

/** El nombre de una tienda del scatter. Sin nombre se usa la cola del id, como en el ranking. */
export function scatterStoreLabel(point: StoreCohortPoint): string {
    return point.storeName ?? `Tienda ${point.storeId.slice(-6)}`;
}

/**
 * Los puntos que se pueden ubicar, del más grande al más chico.
 *
 * Sin cualquiera de los dos ejes el punto no tiene coordenada y se descarta: pintarlo en el origen
 * lo pondría en PROBLEMA, que es una acusación que el dato no sostiene.
 */
export function plottableStorePoints(points: StoreCohortPoint[]): StoreCohortPoint[] {
    return points
        .filter((point) => point.sciAvg !== null && point.cohortPct !== null)
        .sort((a, b) => b.sureKeepUnits - a.sureKeepUnits);
}

/** El punto más grande de la ventana. Nunca cero, o el radio se indefine. */
export function maxSureKeepUnits(points: StoreCohortPoint[]): number {
    const max = Math.max(0, ...points.map((point) => point.sureKeepUnits));
    return max === 0 ? 1 : max;
}

/** Radio del punto según su volumen SureKeep. Los cortes del mock: de 4 a 18 px del viewBox. */
export function scatterRadius(units: number, maxUnits: number): number {
    return 4 + (Math.min(units, maxUnits) / maxUnits) * 14;
}

/**
 * Color del punto según su captura: gris a los 10 % y azul fuerte a los 60 %, interpolado.
 * `null` es el gris de arranque — no hay captura que colorear, y el punto igual existe.
 */
export function captureColor(capturePct: number | null): string {
    const value = capturePct ?? 10;
    const t = Math.max(0, Math.min(1, (value - 10) / 50));
    const hue = 215 + (217 - 215) * t;
    const saturation = 8 + (91 - 8) * t;
    const lightness = 65 - (65 - 45) * t;
    return `hsl(${String(Math.round(hue))} ${String(Math.round(saturation))}% ${String(Math.round(lightness))}%)`;
}

/** Las dos reglas de anomalía que hoy tienen dato. La tercera del mock necesita los rechazos. */
export type OriginationAnomalyKind = 'CAPTURE_DROP' | 'POOR_COHORT';

export interface OriginationAnomaly {
    key: string;
    kind: OriginationAnomalyKind;
    storeId: string;
    title: string;
    detail: string;
}

/**
 * Las anomalías de la ventana, agrupadas por regla y alfabéticas dentro de cada grupo.
 *
 * ⚠️ La muestra mínima del mock son 3 SOLICITUDES evaluadas, que no se sirven. Acá el guarda son 3
 * VENTAS de la tienda: sin él, una tienda con una sola venta dispara una caída de 100 pp.
 */
export function originationAnomalies(rows: StoreOriginationRow[]): OriginationAnomaly[] {
    const byName = (a: StoreOriginationRow, b: StoreOriginationRow) =>
        storeLabel(a).localeCompare(storeLabel(b), 'es-MX');

    const captureDrops = rows
        .filter(
            (row) =>
                row.channels.total.units >= ANOMALY_MIN_SAMPLE &&
                row.captureChangePp !== null &&
                row.captureChangePp < ANOMALY_CAPTURE_DROP_PP,
        )
        .sort(byName)
        .map((row) => captureDropAnomaly(row));

    const poorCohorts = rows
        .filter(
            (row) =>
                row.firstInstallmentEligibleCredits >= ANOMALY_MIN_SAMPLE &&
                row.firstInstallmentCohortPct !== null &&
                row.firstInstallmentCohortPct < COHORT_HEALTHY_PCT,
        )
        .sort(byName)
        .map((row) => poorCohortAnomaly(row));

    return [...captureDrops, ...poorCohorts];
}

function captureDropAnomaly(row: StoreOriginationRow): OriginationAnomaly {
    const changePp = row.captureChangePp ?? 0;
    const now = row.capturePct;
    const before = now === null ? null : now - changePp;
    return {
        key: `CAPTURE_DROP-${row.storeId}`,
        kind: 'CAPTURE_DROP',
        storeId: row.storeId,
        title: `${storeLabel(row)} · la captura cayó ${String(Math.round(Math.abs(changePp)))} pp`,
        detail: `Antes ${formatPct(roundPct(before))} → ahora ${formatPct(roundPct(now))} · ${formatUnits(row.channels.total.units)} créditos totales, ${formatUnits(row.channels.sureKeep.units)} con SureKeep`,
    };
}

function poorCohortAnomaly(row: StoreOriginationRow): OriginationAnomaly {
    return {
        key: `POOR_COHORT-${row.storeId}`,
        kind: 'POOR_COHORT',
        storeId: row.storeId,
        title: `${storeLabel(row)} · pagó la 1ª cuota a 14d en ${formatPct(roundPct(row.firstInstallmentCohortPct))}`,
        detail: `${formatUnits(row.firstInstallmentEligibleCredits)} créditos elegibles · umbral saludable ≥ ${String(COHORT_HEALTHY_PCT)}%`,
    };
}

/** El texto de la anomalía va en enteros, como el mock. `null` sigue siendo `null`, no un cero. */
function roundPct(pct: number | null): number | null {
    return pct === null ? null : Math.round(pct);
}
